import React, {Component} from 'react';
import * as styles from './IdPSelection.module.css';
import {Button, NavBar, NavText, Text} from "@amzn/fulfillment-hig-components";
import qs from 'query-string';
import buildUrl from 'build-url';
import {emitCounter, MetricMethod, StepMetric} from 'src/metrics';

interface State {
    idp_list: IdPList[];
    redirect_uri: string;
    state?: string;
    scope: string;
    nonce?: string;
    client_id: string;
    response_type: string;
    authorize_url: string;
    user_code?: string;
    code_challenge?: string;
    code_challenge_method?: string;
    referrer?: string;
    ui_locales?: string;
    device_id?: string;
    site_id?: string;
    is_braveheart?: string;
    otp?: string;
}

interface IdPList {
    id: string;
    displayString: string;
}

const valid_display_strings = [
    "",
    "it service login",
    "uwc service login",
    "mma cognito+userpool",
    "login with authgard",
    "login with authportal",
    "login with federate",
    "login with fitpis",
    "login with fitpis beta",
    "login with fuse authportal",
    "login with fuse authportal pool",
    "login with idprism",
    "login with maask",
    "login with maask badge",
    "login with maask alpha",
    "login with maask prod na",
    "login with midway",
    "login with milo",
    "login with milo test",
    "login with milo integ",
    "login with testidp1",
    "login with yubikey"
]

const valid_authorize_url_hosts = [
    "alpha.us-west-2.federated-auth.aft.a2z.com",
    "beta.us-west-2.federated-auth.aft.a2z.com",
    "prod.us-east-1.federated-auth.aft.a2z.com",
    "prod.eu-west-1.federated-auth.aft.a2z.com",
    "prod.us-west-2.federated-auth.aft.a2z.com",
    "prod.ap-northeast-1.federated-auth.aft.a2z.com",
    "prod.eu-south-2.federated-auth.aft.a2z.com"
]

const HTTPS_PROTOCOL = "https:"

/**
 * Validates that authorize_url is an absolute https:// URL pointing at an allowlisted FIT
 * authorization host, before it is used to build the value assigned to
 * window.location.href.
 *
 * Parsing is delegated to the WHATWG URL API (polyfilled via core-js for IE11 in
 * src/polyfills.ts) rather than splitting the string by hand. Every check below is
 * load-bearing -- URL parsing alone does NOT make the value safe:
 *
 *   javascript://<allowed-host>/%0Aalert(1)//  -> protocol 'javascript:', hostname <allowed-host>
 *   https://evil.com@<allowed-host>/           -> protocol 'https:',      hostname <allowed-host>
 *   https://<allowed-host>:8443/               -> protocol 'https:',      hostname <allowed-host>
 *
 * All three yield an allowlisted `hostname`, so a hostname-only check is bypassable. The
 * first is the reported vulnerability: '//<allowed-host>/' is a JavaScript line comment
 * and the encoded newline terminates it, so the remainder executes on navigation. Do not
 * remove the protocol, credential or port checks.
 */
export function isValidAuthorizeUrl(authorize_url?: string | null): boolean {
    if (!authorize_url) {
        return false
    }
    let url: URL
    try {
        url = new URL(authorize_url)
    } catch (e) {
        // Relative or otherwise unparseable values.
        return false
    }
    return url.protocol === HTTPS_PROTOCOL
        && url.username === ""
        && url.password === ""
        && url.port === ""
        && valid_authorize_url_hosts.indexOf(url.hostname) > -1
}

class IdPSelection extends Component<{}, State> {
    state: State = {
        idp_list: [],
        redirect_uri: '',
        state: '',
        scope: '',
        nonce: '',
        client_id: '',
        response_type: '',
        authorize_url: '',
        user_code: '',
        code_challenge: '',
        code_challenge_method: '',
        referrer: '',
        ui_locales: '',
        device_id: '',
        site_id: '',
        is_braveheart: '',
        otp: ''
    };

    // React lifecycle method. Gets called when the component output has been rendered to the DOM.
    componentDidMount() {
        // Start counter: the IdP-selection step was shown to the user.
        emitCounter(MetricMethod.IdPSelection, StepMetric.Start);
        const query_parameters = qs.parse(window.location.search)

        if (this.hasValidJsonStructure(query_parameters.data)) {
            const idp_list: IdPList[] = JSON.parse(query_parameters.data as string);
            this.setState({
                idp_list: idp_list,
                redirect_uri: query_parameters.redirect_uri as string,
                state: query_parameters.state as string,
                scope: query_parameters.scope as string,
                nonce: query_parameters.nonce as string,
                client_id: query_parameters.client_id as string,
                response_type: query_parameters.response_type as string,
                authorize_url: query_parameters.authorize_url as string,
                user_code: query_parameters.user_code as string,
                code_challenge: query_parameters.code_challenge as string,
                code_challenge_method: query_parameters.code_challenge_method as string,
                referrer: query_parameters.referrer as string,
                ui_locales: query_parameters.ui_locales as string,
                device_id: query_parameters.device_id as string,
                site_id: query_parameters.site_id as string,
                is_braveheart: query_parameters.is_braveheart as string,
                otp: query_parameters.otp as string
            })
            // Failure counter: valid payload but the request cannot produce a
            // hand-off (empty IdP list, invalid authorize_url, or invalid
            // display strings).
            if (!this.isRenderableRequest(idp_list, query_parameters.authorize_url)) {
                emitCounter(MetricMethod.IdPSelection, StepMetric.Failure);
            }
        } else {
            // Failure counter: the request payload is missing or malformed.
            emitCounter(MetricMethod.IdPSelection, StepMetric.Failure);
        }
    }

    // Whether the parsed request can render a valid IdP hand-off. Mirrors the
    // condition in renderIdps() so Failure can be emitted once from
    // componentDidMount rather than as a side effect during render.
    isRenderableRequest(idp_list: IdPList[], authorize_url?: string[] | string | null): boolean {
        return !!idp_list
            && idp_list.length > 0
            && isValidAuthorizeUrl(authorize_url as string)
            && idp_list.every(val => valid_display_strings.includes(val.displayString.toLowerCase()));
    }

    hasValidJsonStructure(str?: string[] | string | null) {
        try {
            if (str == undefined || Array.isArray(str)) {
                return false;
            } else {
                JSON.parse(str);
            }
        } catch (error) {
            return false
        }
        return true
    }

    render() {
        const {idp_list, redirect_uri, state, scope, nonce, client_id, response_type, authorize_url, user_code, code_challenge, code_challenge_method, referrer, ui_locales, device_id, site_id, is_braveheart, otp} = this.state

        const authorize_redirect_url = isValidAuthorizeUrl(authorize_url) ? buildUrl(authorize_url, {
            queryParams: {
                redirect_uri: redirect_uri,
                state: state ?? '',
                scope: scope,
                nonce: nonce ?? '',
                response_type: response_type,
                client_id: client_id,
                user_code: user_code ?? '',
                code_challenge: code_challenge ?? '',
                code_challenge_method: code_challenge_method ?? '',
                referrer: referrer ?? '',
                ui_locales : ui_locales ?? '',
                device_id: device_id ?? '',
                site_id: site_id ?? '',
                is_braveheart: is_braveheart ?? '',
                otp: otp ?? '',
                login_hint: ''
                // login_hint must be last parameter, so the IdP id can be appended to it below in renderIdPs
            }
        }) : "";

        const renderIdps = () => {
            if (this.isRenderableRequest(idp_list, authorize_url)) {
                return (
                    idp_list.map((value, index) => {
                        return (
                            <div className={styles.selection} key={index}>
                                <div className={styles.button}>
                                    <Button
                                        size={"lg"} fluid={true}
                                        // Redirecting to /authorize endpoint with login_hint parameter
                                        onClick={() => {
                                            // Success counter: an IdP was selected and the user is handed off.
                                            emitCounter(MetricMethod.IdPSelection, StepMetric.Success);
                                            window.location.href = authorize_redirect_url + value.id
                                        }}>
                                        {value.displayString}
                                    </Button>
                                </div>
                            </div>
                        )
                    })
                )
            } else {
                return <Text>There seems to be a issue with the request. Kindly try logging in from the application again. If the problem
                    persists, Cut a ticket to CTI:Distribution Center/FAAS/Authentication</Text>
            }
        }

        return (
            <div>
                <NavBar>
                    <NavText>
                        <Text size="lg">IdPSelection</Text>
                    </NavText>
                </NavBar>
                <div className={styles.parent}>
                    <div className={styles.hero_image}>
                        {/*Hiding the hero image, since we dont have any image to display.
                        After discussion with AFT UX team we can re-enable it*/}
                        <p style={{display: 'none'}}>[Hero Image Here]</p>
                    </div>
                    <div className={styles.options_container}>
                        <Text size="lg">Sign in using one of the options below.</Text>
                        <br/>
                        {renderIdps()}
                    </div>
                </div>
            </div>
        )
    }
}

export default IdPSelection;